import { useState, useCallback } from "react";
import { Upload, Camera, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AnalysisData } from "@/pages/Index";
import ExifReader from "exifreader";

interface ImageUploaderProps {
  onAnalysisComplete: (data: AnalysisData, url: string) => void;
  isAnalyzing: boolean;
  setIsAnalyzing: (value: boolean) => void;
}

export const ImageUploader = ({ 
  onAnalysisComplete, 
  isAnalyzing, 
  setIsAnalyzing 
}: ImageUploaderProps) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const extractExifData = async (file: File) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const tags = ExifReader.load(arrayBuffer);
      
      return {
        make: tags.Make?.description,
        model: tags.Model?.description,
        dateTime: tags.DateTime?.description,
        focalLength: tags.FocalLength?.description,
        iso: tags.ISOSpeedRatings?.description,
        exposureTime: tags.ExposureTime?.description,
        fNumber: tags.FNumber?.description,
        gps: tags.GPSLatitude && tags.GPSLongitude ? {
          latitude: tags.GPSLatitude.description,
          longitude: tags.GPSLongitude.description,
        } : undefined,
      };
    } catch (error) {
      console.log("No EXIF data found or error reading EXIF:", error);
      return undefined;
    }
  };

  const analyzeImage = async (file: File) => {
    setIsAnalyzing(true);
    
    try {
      // Extract EXIF data
      const exifData = await extractExifData(file);
      
      // Convert image to base64
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      await new Promise((resolve) => {
        reader.onload = resolve;
      });
      
      const base64Image = reader.result as string;

      // Call edge function for AI analysis
      const { data, error } = await supabase.functions.invoke('analyze-image', {
        body: { image: base64Image }
      });

      if (error) {
        console.error('Analysis error:', error);
        
        // Handle rate limiting errors
        if (error.message?.includes('429') || error.message?.includes('rate limit')) {
          toast.error("Rate limit reached. Please try again in a moment.");
          return;
        }
        
        // Handle payment errors
        if (error.message?.includes('402') || error.message?.includes('payment')) {
          toast.error("Service temporarily unavailable. Please try again later.");
          return;
        }
        
        throw error;
      }

      // Combine EXIF with AI analysis
      const analysisData: AnalysisData = {
        ...data,
        exif: exifData,
      };

      onAnalysisComplete(analysisData, base64Image);
      toast.success("Analysis complete!");
    } catch (error) {
      console.error('Error analyzing image:', error);
      const errorMessage = error instanceof Error ? error.message : "Failed to analyze image";
      
      // Don't show generic error if we already showed a specific one
      if (!errorMessage.includes('Rate limit') && !errorMessage.includes('payment')) {
        toast.error("Failed to analyze image. Please try again.");
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleFileSelect = useCallback((file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setSelectedFile(file);
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleClear = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setSelectedFile(null);
  };

  return (
    <Card className="p-6 space-y-4">
      <div className="space-y-2">
        <h2 className="text-xl font-semibold text-foreground">Upload Image</h2>
        <p className="text-sm text-muted-foreground">
          Drag & drop an image or click to browse
        </p>
      </div>

      {!preview ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-border rounded-lg p-12 text-center hover:border-primary transition-colors cursor-pointer"
        >
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }}
            className="hidden"
            id="file-upload"
          />
          <label htmlFor="file-upload" className="cursor-pointer">
            <div className="space-y-4">
              <div className="flex justify-center">
                <Upload className="h-12 w-12 text-muted-foreground" />
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-muted-foreground">
                  PNG, JPG, WEBP up to 10MB
                </p>
              </div>
            </div>
          </label>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="relative rounded-lg overflow-hidden border border-border">
            <img
              src={preview}
              alt="Preview"
              className="w-full h-auto max-h-96 object-contain"
            />
            <Button
              size="icon"
              variant="destructive"
              className="absolute top-2 right-2"
              onClick={handleClear}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <Button
            onClick={() => selectedFile && analyzeImage(selectedFile)}
            disabled={isAnalyzing}
            className="w-full"
            size="lg"
          >
            {isAnalyzing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Camera className="mr-2 h-4 w-4" />
                Analyze Image
              </>
            )}
          </Button>
        </div>
      )}
    </Card>
  );
};
